import type { CanvasCard, DataTableCardData, PageLayout, ReportPage, TableCardConfig, TableRow } from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

export interface SplitPageMeta extends ReportPage {
  isContinuation: boolean;
  isSpillover?: boolean;
  originalPageIndex: number;
}

/** Mirrors TIER_STYLES from DataTableCard — used to estimate row heights. */
export const TABLE_TIER_METRICS: Record<string, { bodyFont: number; headerFont: number; padV: number; padH: number }> =
  {
    F: { bodyFont: 13, headerFont: 11, padV: 10, padH: 10 },
    M: { bodyFont: 11, headerFont: 9, padV: 8, padH: 12 },
    S: { bodyFont: 9, headerFont: 8, padV: 6, padH: 9 }
  };

export function getTableTier(widthPercent: number): string {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

/** Width consumed by expand/collapse toggle icon (14px) + flex gap (6px). */
const TOGGLE_ICON_WIDTH = 20;
/** Extra left padding per nesting level for child rows (matches DataTableCard). */
const INDENT_PER_DEPTH = 12;
/**
 * Minimum content height of the expand/collapse toggle icon rendered in edit mode.
 * The icon uses fontSize:12 + lineHeight:1.5 → 18px, which exceeds text height at
 * S (14px) and M (17px) tiers, adding 4px and 1px respectively per parent row.
 */
export const TOGGLE_ICON_MIN_H = Math.ceil(12 * 1.5);

/**
 * Estimates a single row's pixel height based on actual cell content.
 * Finds the cell with the most text wrapping and uses it to determine
 * the row height. This is critical for medium/small cards where columns
 * are narrow and text wraps heavily.
 *
 * `colTextWidths` can be a single number (uniform) or an array (per-column).
 * `depth` accounts for child-row indentation and parent-row toggle icons
 * that reduce the available text width in the first column.
 */
export function estimateRowH(
  row: TableRow,
  colKeys: string[],
  colTextWidths: number | number[],
  charWidth: number,
  singleLineH: number,
  padV: number,
  depth: number = 0,
  parentIconMinH: number = 0
): number {
  // Only the first column (Name) wraps text. Data columns use white-space: nowrap,
  // so they are always 1 line regardless of content length.
  let maxLines = 1;
  const title = row.title || "";
  if (title.length > 1) {
    let tw = typeof colTextWidths === "number" ? colTextWidths : colTextWidths[0];
    // Child rows have extra left padding: depth * 12px (matches DataTableCard indentation)
    if (depth > 0) tw -= depth * INDENT_PER_DEPTH;
    // Parent rows with children render an expand/collapse toggle that reduces text width.
    // Only applied in edit mode (parentIconMinH > 0) since preview mode doesn't show the icon.
    if (parentIconMinH > 0 && row.children?.length) tw -= TOGGLE_ICON_WIDTH;
    tw = Math.max(tw, 1);

    // Use word-boundary wrapping estimation to match CSS whiteSpace:"normal" +
    // wordBreak:"normal" rendering. CSS also breaks at hyphens, so
    // "Fixed-Income" can wrap as "Fixed-" / "Income". Split on both spaces
    // and after hyphens (lookbehind keeps the hyphen with the left part).
    if (title.includes(" ") || title.includes("-")) {
      const words = title.split(/(?<=-)|\s+/).filter((w) => w.length > 0);
      let usedWidth = 0;
      let lines = 1;
      for (const word of words) {
        const wordW = word.length * charWidth;
        if (usedWidth > 0 && usedWidth + charWidth + wordW > tw) {
          lines++;
          usedWidth = wordW;
        } else {
          usedWidth += (usedWidth > 0 ? charWidth : 0) + wordW;
        }
      }
      maxLines = Math.max(maxLines, lines);
    } else {
      // Single word without hyphens — CSS won't break it, so it's always 1 line
      // (may overflow the cell but doesn't increase row height)
      maxLines = Math.max(maxLines, 1);
    }
  }

  // In edit mode, parent rows render a toggle icon whose height (18px) can exceed
  // the text line height at S (14px) and M (17px) tiers.
  let contentH = Math.ceil(singleLineH * maxLines);
  if (parentIconMinH > 0 && row.children?.length) {
    contentH = Math.max(contentH, parentIconMinH);
  }

  // +1 for the row border, +2 safety buffer for sub-pixel rounding,
  // border-collapse adjustments, and font metric variations.
  return contentH + 2 * padV + 3;
}

export function getHeaderRowH(
  headerFont: number,
  padV: number,
  colKeys?: string[],
  colTitles?: string[],
  colTextWidths?: number | number[]
): number {
  const singleLineH = Math.ceil(headerFont * 1.5);
  if (!colKeys || !colTitles || colTextWidths == null) {
    return singleLineH + 2 * padV + 2;
  }
  // Headers use word-break: normal + overflow: hidden — only multi-word
  // titles wrap (at spaces). Single-word titles stay on one line.
  const charWidth = headerFont * 0.65; // uppercase tends wider
  let maxLines = 1;
  for (let i = 0; i < colKeys.length; i++) {
    const text = colTitles[i] || colKeys[i] || "";
    if (!text.includes(" ") && !text.includes("-")) continue; // single-word headers don't wrap
    const tw = typeof colTextWidths === "number" ? colTextWidths : colTextWidths[i];
    // Split on spaces and hyphens (CSS wordBreak:"normal" breaks at hyphens)
    const words = text.split(/(?<=-)|\s+/).filter((w) => w.length > 0);
    let usedWidth = 0;
    let lines = 1;
    for (const word of words) {
      const wordW = word.length * charWidth;
      if (usedWidth > 0 && usedWidth + charWidth + wordW > tw) {
        lines++;
        usedWidth = wordW;
      } else {
        usedWidth += (usedWidth > 0 ? charWidth : 0) + wordW;
      }
    }
    maxLines = Math.max(maxLines, lines);
  }
  return Math.ceil(singleLineH * maxLines) + 2 * padV + 2;
}

// ─── Expand-aware height helpers ────────────────────────────────────────────

const EMPTY_SET: Set<string> = new Set();
const EMPTY_OFFSETS: Record<string, number> = {};

/**
 * Sum pixel heights of visible rows including children of expanded rows.
 * `depth` tracks nesting level so `rowHFn` can account for indentation.
 */
export function measureVisibleHeight(
  rows: TableRow[],
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow, depth: number) => number,
  depth: number = 0
): number {
  const offset = offsets[parentKey] ?? 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const rowKey = `${parentKey}-${i + offset}`;
    total += rowHFn(rows[i], depth);
    if (rows[i].children?.length && expanded.has(rowKey)) {
      total += measureVisibleHeight(rows[i].children!, expanded, rowKey, offsets, rowHFn, depth + 1);
    }
  }
  return total;
}

interface SliceResult {
  taken: TableRow[];
  remaining: TableRow[];
  remainingOffsets: Record<string, number>;
}

/**
 * Slice rows so the total visible pixel height fits within `budget`.
 * When a parent's expanded children don't fully fit, splits the children
 * across pages rather than moving the entire parent.
 *
 * The first row is always taken even if it exceeds the budget, to guarantee
 * forward progress and avoid infinite loops.
 */
function sliceByHeight(
  rows: TableRow[],
  budget: number,
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow, depth: number) => number,
  depth: number = 0
): SliceResult {
  const topOffset = offsets[parentKey] ?? 0;
  let used = 0;

  for (let i = 0; i < rows.length; i++) {
    const origIdx = i + topOffset;
    const rowKey = `${parentKey}-${origIdx}`;
    const row = rows[i];
    const rowH = rowHFn(row, depth);
    const hasExpandedChildren = !!(row.children?.length && expanded.has(rowKey));

    if (!hasExpandedChildren) {
      if (used + rowH > budget && i > 0) {
        return {
          taken: rows.slice(0, i),
          remaining: rows.slice(i),
          remainingOffsets: { [parentKey]: origIdx }
        };
      }
      used += rowH;
      continue;
    }

    // Parent with expanded children
    const childrenH = measureVisibleHeight(row.children!, expanded, rowKey, offsets, rowHFn, depth + 1);
    const totalCost = rowH + childrenH;

    if (used + totalCost <= budget) {
      used += totalCost;
      continue;
    }

    // Doesn't fit entirely — try to split children
    const remainingBudget = budget - used - rowH;

    if (remainingBudget > 0) {
      const childResult = sliceByHeight(row.children!, remainingBudget, expanded, rowKey, offsets, rowHFn, depth + 1);

      if (childResult.taken.length > 0) {
        // Verify taken children actually fit within the budget.
        // sliceByHeight's first-row guarantee may produce oversized results
        // when a single child row exceeds the available space — in that case,
        // move the entire parent row to the next page instead of overflowing.
        const takenChildrenH = measureVisibleHeight(childResult.taken, expanded, rowKey, offsets, rowHFn, depth + 1);
        if (takenChildrenH > remainingBudget && i > 0) {
          return {
            taken: rows.slice(0, i),
            remaining: rows.slice(i),
            remainingOffsets: { [parentKey]: origIdx }
          };
        }

        const takenRow = { ...row, children: childResult.taken };
        const taken = [...rows.slice(0, i), takenRow];

        if (childResult.remaining.length > 0) {
          const remainingRow = { ...row, children: childResult.remaining };
          return {
            taken,
            remaining: [remainingRow, ...rows.slice(i + 1)],
            remainingOffsets: {
              [parentKey]: origIdx,
              ...childResult.remainingOffsets
            }
          };
        }

        return {
          taken,
          remaining: rows.slice(i + 1),
          remainingOffsets: { [parentKey]: origIdx + 1 }
        };
      }
    }

    // No room for parent + any children
    if (i > 0) {
      return {
        taken: rows.slice(0, i),
        remaining: rows.slice(i),
        remainingOffsets: { [parentKey]: origIdx }
      };
    }

    // First row doesn't fit — take it anyway to avoid infinite loop
    used += totalCost;
  }

  return {
    taken: rows,
    remaining: [],
    remainingOffsets: {}
  };
}

// ─── Card height estimation ─────────────────────────────────────────────────

/** Minimum rows on a page before a table split is allowed. If fewer rows would
 *  fit, push the entire table to the next page instead of "half-cutting" it. */
const MIN_ROWS_BEFORE_SPLIT = 3;

/** PreviewCardWrapper adds a 1px border (top + bottom = 2px) per card. */
const CARD_BORDER_PX = 2;

/**
 * Estimate a non-table card's rendered height.
 * Uses grid height (gridH * GRID_CELL_HEIGHT) when available,
 * explicit `heightPx` when set, otherwise falls back to type-based defaults.
 */
function estimateCardHeight(card: CanvasCard): number {
  if (card.gridH > 0) return card.gridH * GRID_CONSTANTS.GRID_CELL_HEIGHT;
  if (card.heightPx != null) return card.heightPx;
  switch (card.config.type) {
    case "kpi":
      return 90;
    case "summary":
      return 110;
    case "text":
      return 120;
    case "chart":
    case "exposure":
      return 240;
    case "cashflow":
    case "performance":
    case "unfunded":
    case "macroBrief":
      return 200;
    case "benchmark":
    case "dataFreshness":
      return 160;
    case "ai_card":
      return 150;
    default:
      return 200;
  }
}

/**
 * Group a flat list of cards into visual rows using the same width-accumulation
 * logic that PageView and PreviewMode use for rendering.
 */
function groupIntoVisualRows(cards: CanvasCard[]): CanvasCard[][] {
  const rows: CanvasCard[][] = [];
  let current: CanvasCard[] = [];
  let w = 0;
  for (const card of cards) {
    if (w > 0 && w + card.widthPercent > 100.01) {
      rows.push(current);
      current = [];
      w = 0;
    }
    current.push(card);
    w += card.widthPercent;
  }
  if (current.length > 0) rows.push(current);
  return rows;
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function isTableCardWithData(card: CanvasCard): card is CanvasCard & { data: DataTableCardData } {
  if (card.config.type !== "table" || !card.data) return false;
  const d = card.data as DataTableCardData;
  return Array.isArray(d.data) && Array.isArray(d.columns);
}

export interface SplitResult {
  pages: CanvasCard[][];
  pageMeta: SplitPageMeta[];
  /** Per-card index offsets for DataTableCard key generation. */
  cardOffsets: Record<string, Record<string, number>>;
  /** Maps continuation card IDs back to their original card ID. */
  originalCardIds: Record<string, string>;
}

export function splitPagesForOverflow(
  groupedPages: CanvasCard[][],
  pages: ReportPage[],
  pageHeight: number,
  pageWidth: number,
  expandedRows?: Record<string, Set<string>>,
  options?: { contentPadding?: number; isEditMode?: boolean }
): SplitResult {
  const resolvedPages: CanvasCard[][] = [];
  const resolvedMeta: SplitPageMeta[] = [];
  const cardOffsets: Record<string, Record<string, number>> = {};
  const originalCardIds: Record<string, string> = {};

  for (let pageIdx = 0; pageIdx < groupedPages.length; pageIdx++) {
    const pageCards = groupedPages[pageIdx];
    const page = pages[pageIdx];
    const layout = page.layout;

    if (layout === "title") {
      resolvedPages.push(pageCards);
      resolvedMeta.push({ ...page, isContinuation: false, originalPageIndex: pageIdx });
      continue;
    }

    const headerH = layout === "header-content" ? 180 : layout === "section" ? 150 : 0;
    const footerH = 52;
    const padVertical = 80; // 32px top + 48px bottom content padding
    const safetyBuffer = 20; // accounts for estimation errors, borders, etc.
    const rowGap = 20;
    const contentOnlyAvailH = pageHeight - footerH - padVertical - safetyBuffer;

    let currentCards: CanvasCard[] = [];
    let usedH = 0;
    let currentAvailH = pageHeight - headerH - footerH - padVertical - safetyBuffer;
    let currentMeta: SplitPageMeta = { ...page, isContinuation: false, originalPageIndex: pageIdx };

    const flushPage = () => {
      if (currentCards.length > 0) {
        resolvedPages.push(currentCards);
        resolvedMeta.push(currentMeta);
      }
      currentCards = [];
      usedH = 0;
      currentAvailH = contentOnlyAvailH;
      currentMeta = { layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx };
    };

    // Group cards into visual rows so side-by-side cards share one row height.
    // Sort by grid position first so cards are processed in spatial order,
    // preventing non-table cards from being swept onto continuation pages.
    const sortedPageCards = [...pageCards].sort((a, b) => a.gridY - b.gridY || a.gridX - b.gridX);
    const visualRows = groupIntoVisualRows(sortedPageCards);

    for (const visualRow of visualRows) {
      // Check if this row contains a splittable table
      const tableCardIdx = visualRow.findIndex((c) => isTableCardWithData(c));

      if (tableCardIdx === -1) {
        // ── Non-table row: height = tallest card in the row + border ──
        const rowH = Math.max(...visualRow.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;

        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // ── Row contains a table card ──
      const card = visualRow[tableCardIdx];
      const companionCards = visualRow.filter((_, i) => i !== tableCardIdx);
      const companionH =
        companionCards.length > 0 ? Math.max(...companionCards.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX : 0;

      const tableData = (card as CanvasCard & { data: DataTableCardData }).data;
      // Move "Total" row to the end to match DataTableCard rendering order
      const rawRows = tableData.data;
      const totalIdx = rawRows.findIndex((r) => r.title?.toLowerCase() === "total");
      const tableRows =
        totalIdx > -1 && totalIdx < rawRows.length - 1
          ? [...rawRows.slice(0, totalIdx), ...rawRows.slice(totalIdx + 1), rawRows[totalIdx]]
          : rawRows;

      if (tableRows.length === 0) {
        const rowH = Math.max(60, companionH) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;
        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // Compute per-row height metrics for this table card
      const tier = getTableTier(card.widthPercent);
      const { bodyFont, headerFont, padV, padH } = TABLE_TIER_METRICS[tier];
      const outerPad = options?.contentPadding ?? 96;
      const availableW = pageWidth - outerPad;
      const colW = (availableW - (GRID_CONSTANTS.GRID_COLUMNS - 1) * GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS;
      const cardWidthPx = colW * card.gridW + (card.gridW - 1) * GRID_CONSTANTS.GAP;
      const colKeys = tableData.columns.map((col: string | { key: string }) =>
        typeof col === "string" ? col : col.key
      );
      const colTitles = tableData.columns.map((col: string | { key: string; title?: string }) =>
        typeof col === "string" ? col : col.title || col.key
      );
      const colCount = colKeys.length;
      const charWidth = bodyFont * 0.65;
      const singleLineH = Math.ceil(bodyFont * 1.5);

      // Use actual column widths when available for accurate wrapping estimation
      const storedWidths = (card.config as TableCardConfig).columnWidths;
      let colTextWidths: number | number[];
      if (storedWidths && colKeys.every((k) => storedWidths[k] != null)) {
        const rawPcts = colKeys.map((k) => storedWidths[k]);
        const sum = rawPcts.reduce((a, b) => a + b, 0);
        colTextWidths = rawPcts.map((pct) => Math.max((pct / sum) * cardWidthPx - 2 * padH, 10));
      } else {
        const colWidthPx = cardWidthPx / Math.max(colCount, 1);
        colTextWidths = Math.max(colWidthPx - 2 * padH, 10);
      }

      const iconMinH = options?.isEditMode ? TOGGLE_ICON_MIN_H : 0;
      const rowHFn = (r: TableRow, depth: number) =>
        estimateRowH(r, colKeys, colTextWidths, charWidth, singleLineH, padV, depth, iconMinH);

      const headerRowH = getHeaderRowH(headerFont, padV, colKeys, colTitles, colTextWidths);
      const tablePad = 8;

      const cardExpanded = expandedRows?.[card.id] ?? EMPTY_SET;
      const totalContentH = measureVisibleHeight(tableRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
      const totalTableH = tablePad + headerRowH + totalContentH + CARD_BORDER_PX;
      const gap = currentCards.length > 0 ? rowGap : 0;
      const spaceForRow = currentAvailH - usedH - gap;

      // If the whole table (and companion cards) fit, add the row
      if (Math.max(totalTableH, companionH) <= spaceForRow) {
        usedH += gap + Math.max(totalTableH, companionH);
        currentCards.push(...visualRow);
        continue;
      }

      // ── Split required ──
      const spaceForTableRows = spaceForRow - tablePad - headerRowH - CARD_BORDER_PX;

      let remaining: TableRow[];
      let currentOffsets: Record<string, number> = {};

      if (spaceForTableRows > 0) {
        const sliceResult = sliceByHeight(tableRows, spaceForTableRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        // Only split here if enough rows fit to look presentable;
        // otherwise push the entire table to the next page.
        if (sliceResult.taken.length >= MIN_ROWS_BEFORE_SPLIT) {
          // First chunk + companion cards stay on the current page
          currentCards.push(...companionCards);
          currentCards.push({
            ...card,
            data: { columns: tableData.columns, data: sliceResult.taken }
          });
          remaining = sliceResult.remaining;
          currentOffsets = sliceResult.remainingOffsets;
        } else {
          remaining = tableRows;
        }
      } else {
        remaining = tableRows;
      }

      // If nothing fit on this page, companions go with the first chunk on next page
      if (remaining === tableRows) {
        flushPage();
        // Try fitting on the fresh page — reset gridY since this is a new page
        const freshSpace = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;
        const freshSlice = sliceByHeight(tableRows, freshSpace, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        currentCards.push(...companionCards.map((c) => ({ ...c, gridY: 0 })));
        if (freshSlice.taken.length > 0) {
          currentCards.push({
            ...card,
            gridY: 0,
            data: { columns: tableData.columns, data: freshSlice.taken }
          });
          remaining = freshSlice.remaining;
          currentOffsets = freshSlice.remainingOffsets;
        } else {
          // Table doesn't even fit a fresh page — push it all to avoid infinite loop
          currentCards.push({ ...card, gridY: 0 });
          remaining = [];
        }
      } else {
        flushPage();
      }

      let chunkIdx = 1;

      while (remaining.length > 0) {
        const contSpaceForRows = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;

        const contResult = sliceByHeight(remaining, contSpaceForRows, cardExpanded, "root", currentOffsets, rowHFn);

        const contCardId = `${card.id}-cont-${chunkIdx}`;
        const contCard: CanvasCard = {
          ...card,
          id: contCardId,
          gridY: 0,
          data: { columns: tableData.columns, data: contResult.taken }
        };

        if (Object.keys(currentOffsets).length > 0) {
          cardOffsets[contCardId] = currentOffsets;
        }
        originalCardIds[contCardId] = card.id;

        if (contResult.remaining.length > 0) {
          resolvedPages.push([contCard]);
          resolvedMeta.push({ layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx });
        } else {
          currentCards.push(contCard);
          const chunkContentH = measureVisibleHeight(contResult.taken, cardExpanded, "root", currentOffsets, rowHFn);
          usedH = tablePad + headerRowH + chunkContentH + CARD_BORDER_PX;
        }

        remaining = contResult.remaining;
        currentOffsets = contResult.remainingOffsets;
        chunkIdx++;
      }

      // After a table split, flush the last continuation chunk and prepare
      // a fresh regular page for any remaining non-table visual rows.
      if (chunkIdx > 1) {
        if (currentCards.length > 0) {
          resolvedPages.push(currentCards);
          resolvedMeta.push(currentMeta);
          currentCards = [];
          usedH = 0;
        }
        currentAvailH = contentOnlyAvailH;
        currentMeta = {
          layout: "content" as PageLayout,
          isContinuation: false,
          isSpillover: true,
          originalPageIndex: pageIdx
        };
      }
    }

    if (currentCards.length > 0 || (!currentMeta.isContinuation && !currentMeta.isSpillover)) {
      // Reset gridY for spillover pages so cards start from the top
      if (currentMeta.isSpillover && currentCards.length > 0) {
        const minY = Math.min(...currentCards.map((c) => c.gridY));
        if (minY > 0) {
          currentCards = currentCards.map((c) => ({ ...c, gridY: c.gridY - minY }));
        }
      }
      resolvedPages.push(currentCards);
      resolvedMeta.push(currentMeta);
    }
  }

  // Post-process: move non-table cards off continuation pages.
  // Continuation pages are display-only artifacts for table overflow — no user cards belong there.
  // When the primary page has a splitting table, stray cards go to a spillover page instead.
  {
    const originalCardMap = new Map<string, CanvasCard>();
    for (const page of groupedPages) {
      for (const card of page) {
        originalCardMap.set(card.id, card);
      }
    }

    const splittingIds = new Set(Object.values(originalCardIds));
    const spilloverByOrigPage = new Map<number, CanvasCard[]>();

    for (let i = resolvedPages.length - 1; i >= 0; i--) {
      if (!resolvedMeta[i].isContinuation) continue;
      const origPageIdx = resolvedMeta[i].originalPageIndex;

      // Cards NOT in originalCardIds are regular user cards (not table overflow chunks)
      const strayCards = resolvedPages[i].filter((c) => !originalCardIds[c.id]);
      if (strayCards.length === 0) continue;

      // Find the primary (non-continuation) page for this originalPageIndex
      const primaryIdx = resolvedMeta.findIndex(
        (m) => m.originalPageIndex === origPageIdx && !m.isContinuation && !m.isSpillover
      );
      if (primaryIdx === -1) continue;

      const hasSplittingTable = resolvedPages[primaryIdx].some((c) => splittingIds.has(c.id));

      for (const stray of strayCards) {
        const original = originalCardMap.get(stray.id);
        const cardToInsert = original ?? stray;

        if (hasSplittingTable) {
          // Collect for spillover page — don't push to primary (no room)
          if (!spilloverByOrigPage.has(origPageIdx)) {
            spilloverByOrigPage.set(origPageIdx, []);
          }
          spilloverByOrigPage.get(origPageIdx)!.push(cardToInsert);
        } else {
          // Check if the card would fit on the primary page before moving it back.
          // In preview mode, different padding/height calculations may cause cards
          // to exceed the visible area and get clipped by overflow:hidden.
          const primaryLayout = resolvedMeta[primaryIdx].layout;
          const pHeaderH = primaryLayout === "header-content" ? 180 : primaryLayout === "section" ? 150 : 0;
          const pAvailH = pageHeight - pHeaderH - 52 - 80 - 20; // footerH + padVertical + safetyBuffer
          const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
          const pMaxRows = Math.floor((pAvailH + GRID_CONSTANTS.GAP) / cellPlusGap);

          if (cardToInsert.gridY + cardToInsert.gridH > pMaxRows) {
            if (!spilloverByOrigPage.has(origPageIdx)) {
              spilloverByOrigPage.set(origPageIdx, []);
            }
            spilloverByOrigPage.get(origPageIdx)!.push(cardToInsert);
          } else {
            resolvedPages[primaryIdx].push(cardToInsert);
          }
        }
      }
      resolvedPages[i] = resolvedPages[i].filter((c) => !!originalCardIds[c.id]);

      // Remove empty continuation pages
      if (resolvedPages[i].length === 0) {
        resolvedPages.splice(i, 1);
        resolvedMeta.splice(i, 1);
      }
    }

    // Create spillover pages for stray cards displaced by splitting tables
    for (const [origPageIdx, spillCards] of spilloverByOrigPage) {
      // Reset gridY so cards start from the top of the new page
      const minY = Math.min(...spillCards.map((c) => c.gridY));
      const resetCards = minY > 0 ? spillCards.map((c) => ({ ...c, gridY: c.gridY - minY })) : spillCards;

      // Check if a spillover page already exists for this originalPageIndex
      const existingSpilloverIdx = resolvedMeta.findIndex((m) => m.originalPageIndex === origPageIdx && m.isSpillover);

      if (existingSpilloverIdx !== -1) {
        resolvedPages[existingSpilloverIdx].push(...resetCards);
      } else {
        // Insert after the last page belonging to this originalPageIndex
        let insertAt = resolvedPages.length;
        for (let j = resolvedPages.length - 1; j >= 0; j--) {
          if (resolvedMeta[j].originalPageIndex === origPageIdx) {
            insertAt = j + 1;
            break;
          }
        }

        resolvedPages.splice(insertAt, 0, resetCards);
        resolvedMeta.splice(insertAt, 0, {
          layout: "content" as PageLayout,
          isContinuation: false,
          isSpillover: true,
          originalPageIndex: origPageIdx
        });
      }
    }
  }

  // Final pass: enforce page ordering invariant.
  // Within each originalPageIndex group the order must be: primary → spillover → continuation.
  // This guarantees a table's first segment (on primary or spillover) always precedes
  // its continuation pages, preventing detached continuations from appearing earlier
  // in the document than the component that generated them.
  {
    const typeOrder = (m: SplitPageMeta) => (m.isContinuation ? 2 : m.isSpillover ? 1 : 0);

    const indexed = resolvedMeta.map((meta, i) => ({ i, meta, page: resolvedPages[i] }));
    indexed.sort((a, b) => {
      if (a.meta.originalPageIndex !== b.meta.originalPageIndex) {
        return a.meta.originalPageIndex - b.meta.originalPageIndex;
      }
      const orderDiff = typeOrder(a.meta) - typeOrder(b.meta);
      if (orderDiff !== 0) return orderDiff;
      return a.i - b.i; // preserve original relative order within same type
    });

    for (let k = 0; k < indexed.length; k++) {
      resolvedPages[k] = indexed[k].page;
      resolvedMeta[k] = indexed[k].meta;
    }
  }

  return { pages: resolvedPages, pageMeta: resolvedMeta, cardOffsets, originalCardIds };
}
